import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  CreateEventBody,
  EventResponse,
  EventStatus,
  Paginated,
  UpdateEventBody,
} from "@/types";

export function usePendingEvents() {
  return useQuery({
    queryKey: ["events", { status: "pending" }],
    queryFn: () =>
      api
        .get<Paginated<EventResponse>>("/admin/events", { status: "pending", limit: "100" })
        .then((r) => r.data),
    refetchInterval: 60_000, // refresh every 60s
  });
}

export function useAllEvents(filters: {
  status?: string;
  search?: string;
  categoryId?: string;
  hidePast?: boolean;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["events", filters],
    queryFn: () => {
      const params: Record<string, string> = {
        status: filters.status || "all",
        limit: String(filters.limit ?? 25),
        isParent: "false",
      };
      if (filters.search) params.search = filters.search;
      if (filters.categoryId) params.category = filters.categoryId;
      if (filters.hidePast) params.hidePast = "true";
      if (filters.cursor) params.cursor = filters.cursor;
      return api.get<Paginated<EventResponse>>("/admin/events", params);
    },
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEventBody) => api.post<EventResponse>("/admin/events", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useDeleteEventsBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await api.delete(`/admin/events/${id}`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useUpdateEventStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      rejectionReason,
    }: {
      id: string;
      status: EventStatus;
      rejectionReason?: string;
    }) => api.put<EventResponse>(`/admin/events/${id}/status`, { status, rejectionReason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateEventBody }) =>
      api.put<EventResponse>(`/admin/events/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/events/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
  });
}
